<div class="mapping-modeller">
  <header class="modeller-heading">
    <div>
      <span class="eyebrow">EDI mapping modeller</span>
      <h3>Design a source-to-Gung mapping instruction</h3>
      <p>Open a saved mapping directly, or create and test a new one against this backend's message classes.</p>
    </div>
    <span class="draft-badge">BETA</span>
  </header>

  <section class="model-settings-shell">
    <header class="model-settings-heading">
      <span>
        <span class="eyebrow">Model settings</span>
        <strong>{{ selectedFile?.name || 'Choose a source document' }}</strong>
        <small>{{ targetMessageType ? 'Target: Gung ' + targetMessageType : 'Choose a runtime target' }}</small>
      </span>
    </header>

    <div class="model-settings-content">
      <section class="model-library-card">
        <label>
          <span>Saved mapping model</span>
          <select
            class="form-control"
            [ngModel]="selectedSavedModelId"
            [disabled]="loadingSavedModel || savingModel || !dryRunCompatible"
            (ngModelChange)="selectedSavedModelId = $event; selectSavedModel($event)"
          >
            <option value="">New mapping model</option>
            <option *ngFor="let saved of savedModels" [value]="saved.modelId">
              {{ saved.name }} · v{{ saved.version }} · {{ saved.sourceCount }} fixture{{
                saved.sourceCount === 1 ? '' : 's'
              }}
            </option>
          </select>
        </label>
        <div class="library-status" *ngIf="savedModel; else newModelStatus">
          <span>Editing next version</span>
          <strong>{{ savedModel.name }} · current v{{ savedModel.version }}</strong>
          <small>Last modified by {{ savedModel.modifiedBy }} at {{ savedModel.modifiedAt | date: 'medium' }}</small>
        </div>
        <ng-template #newModelStatus>
          <div class="library-status">
            <span>New model</span>
            <strong>{{ loadingSavedModel ? 'Loading saved model…' : 'Version 1 will be created on save' }}</strong>
            <small>The suggested name can be changed before the first save.</small>
          </div>
        </ng-template>
      </section>

      <section class="setup-card">
        <label class="file-field">
          <span>Source documents</span>
          <input type="file" multiple (change)="selectFiles($event)" />
          <small>
            {{
              fixtureFiles.length
                ? fixtureFiles.length + ' source fixture' + (fixtureFiles.length === 1 ? '' : 's') + ' selected'
                : 'Choose EDIFACT, X12, XML, JSON, CSV, or text documents'
            }}
          </small>
        </label>

        <div class="source-fixtures" *ngIf="fixtureFiles.length">
          <div *ngFor="let sourceFile of fixtureFiles; let sourceIndex = index">
            <i class="fa-solid" [ngClass]="sourceIndex === 0 ? 'fa-star' : 'fa-file-code'"></i>
            <span [title]="sourceFile.name">{{ sourceFile.name }}</span>
            <small>{{ sourceIndex === 0 ? 'Primary' : 'Dry-run fixture' }}</small>
            <button
              *ngIf="sourceIndex > 0"
              type="button"
              class="btn btn-sm btn-link"
              title="Remove source fixture"
              (click)="removeSourceFile(sourceIndex)"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <label class="add-source-fixtures">
            <i class="fa-solid fa-plus"></i>
            Add test fixtures
            <input type="file" multiple (change)="addSourceFiles($event)" />
          </label>
        </div>

        <label>
          <span>Gung target message</span>
          <select class="form-control" [(ngModel)]="targetMessageType" [disabled]="loading || !!savedModel">
            <option *ngFor="let type of targetMessageTypes" [value]="type">{{ type }}</option>
          </select>
        </label>

        <label class="ai-toggle">
          <input type="checkbox" [(ngModel)]="useAi" />
          <span>
            <strong>Ask AI to propose the instruction</strong>
            <small>The runtime target contract limits which fields AI may map.</small>
          </span>
        </label>

        <label class="guidance-field">
          <span>Guidance or partner deviation</span>
          <textarea
            class="form-control"
            rows="3"
            [(ngModel)]="userInstruction"
            placeholder="Optional: describe partner-specific meanings or deviations from the standard."
          ></textarea>
        </label>

        <button
          type="button"
          class="btn btn-primary create-button"
          [disabled]="!selectedFile || !targetMessageType || loading"
          (click)="createModel(false)"
        >
          <i class="fa-solid mr-1" [ngClass]="loading ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'"></i>
          {{ loading ? 'Creating model…' : useAi ? 'Ask AI to create model' : 'Create mapping model' }}
        </button>
      </section>
    </div>
  </section>

  <div class="alert alert-danger" *ngIf="error">{{ error }}</div>
  <div class="alert alert-warning" *ngIf="compatibilityWarning">{{ compatibilityWarning }}</div>

  <section class="modeller-empty-state" *ngIf="!model && !loadingSavedModel">
    <i class="fa-solid fa-diagram-project" aria-hidden="true"></i>
    <div>
      <h4>{{ savedModels.length ? 'Open a saved mapping or create a new one' : 'Create your first mapping' }}</h4>
      <p *ngIf="savedModels.length">
        Select a saved model above to restore its mapping and source fixtures without uploading the documents again.
      </p>
      <p *ngIf="!savedModels.length">Choose one or more source documents above to start a new mapping model.</p>
    </div>
  </section>

  <ng-container *ngIf="model">
    <section class="mapper-command-bar">
      <div class="command-model">
        <span class="eyebrow">Current draft</span>
        <strong [title]="modelName">{{ modelName || model.source.fileName }}</strong>
        <small>
          {{ model.source.standard || model.source.syntax }} {{ model.source.messageType || '' }}
          <i class="fa-solid fa-arrow-right"></i>
          Gung {{ model.target.messageType }}
        </small>
      </div>
      <div class="mapper-actions">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          [disabled]="validating"
          (click)="validateInstruction()"
        >
          <i class="fa-solid mr-1" [ngClass]="validating ? 'fa-spinner fa-spin' : 'fa-check'"></i>
          {{ validating ? 'Validating…' : 'Validate' }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          [disabled]="dryRunning || !dryRunCompatible"
          (click)="dryRunInstruction()"
        >
          <i class="fa-solid mr-1" [ngClass]="dryRunning ? 'fa-spinner fa-spin' : 'fa-play'"></i>
          {{
            dryRunning
              ? 'Running…'
              : 'Dry run ' + fixtureFiles.length + (fixtureFiles.length === 1 ? ' file' : ' files')
          }}
        </button>
        <button type="button" class="btn btn-sm btn-primary" [disabled]="loading || !useAi" (click)="createModel(true)">
          <i class="fa-solid fa-wand-magic-sparkles mr-1"></i>
          Ask AI
        </button>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          [disabled]="savingModel || !selectedFile || !modelName.trim() || !dryRunCompatible"
          (click)="saveModel()"
        >
          <i class="fa-solid mr-1" [ngClass]="savingModel ? 'fa-spinner fa-spin' : 'fa-floppy-disk'"></i>
          {{ savingModel ? 'Saving…' : savedModel ? 'Save new version' : 'Save model' }}
        </button>
      </div>
    </section>

    <nav class="mapper-workspace-tabs" aria-label="Mapping modeller workspace">
      <button type="button" [class.active]="activeWorkspaceTab === 'map'" (click)="selectWorkspaceTab('map')">
        Map
      </button>
      <button
        type="button"
        [class.active]="activeWorkspaceTab === 'instruction'"
        (click)="selectWorkspaceTab('instruction')"
      >
        Instruction
        <span class="tab-status" [class.good]="validation?.valid" [class.bad]="validation && !validation.valid">
          {{ validation ? (validation.valid ? 'Valid' : 'Needs changes') : 'Not checked' }}
        </span>
      </button>
      <button type="button" [class.active]="activeWorkspaceTab === 'test'" (click)="selectWorkspaceTab('test')">
        Test &amp; results
        <span
          class="tab-status"
          [class.good]="dryRunBatch && dryRunBatch.failed === 0 && !dryRunOutdated"
          [class.bad]="dryRunBatch && dryRunBatch.failed > 0 && !dryRunOutdated"
          [class.stale]="dryRunOutdated"
        >
          {{
            dryRunOutdated
              ? 'Outdated'
              : dryRunBatch
                ? dryRunBatch.passed + '/' + dryRunBatch.total + ' passed'
                : 'Not run'
          }}
        </span>
      </button>
      <button type="button" [class.active]="activeWorkspaceTab === 'history'" (click)="selectWorkspaceTab('history')">
        Versions &amp; prompts
        <span class="tab-status">{{ savedPrompts.length + sessionPrompts.length }}</span>
      </button>
    </nav>

    <div class="alert alert-warning" *ngFor="let warning of model.warnings">{{ warning }}</div>

    <section class="workspace-pane" [hidden]="activeWorkspaceTab !== 'map'">
      <section class="contract-summary">
        <div>
          <span>Detected source</span>
          <strong>{{ model.source.standard || model.source.syntax }} {{ model.source.messageType || '' }}</strong>
          <small>{{ model.source.version || 'No reported version' }}</small>
        </div>
        <i class="fa-solid fa-arrow-right"></i>
        <div>
          <span>Runtime target</span>
          <strong>{{ model.target.messageType }}</strong>
          <small [title]="model.target.contractHash">{{ model.target.javaType }}</small>
        </div>
        <div>
          <span>Instruction origin</span>
          <strong>{{ model.instructionOrigin }}</strong>
          <small>Contract {{ model.target.contractHash | slice: 0 : 19 }}…</small>
        </div>
      </section>

      <section class="mapping-workspace">
        <article class="contract-panel">
          <header>
            <span class="eyebrow">Source contract</span>
            <h5 class="source-file-name" [title]="model.source.fileName">{{ model.source.fileName }}</h5>
            <p>Select a source value to create a manual rule.</p>
          </header>
          <div class="contract-tree">
            <lib-edi-source-tree-node
              *ngFor="let root of model.source.roots"
              [node]="root"
              [depth]="0"
              [selectedNodeIds]="selectedSourceNodeIds"
              (sourceSelected)="selectSource($event)"
            ></lib-edi-source-tree-node>
          </div>
        </article>

        <article class="contract-panel">
          <header>
            <span class="eyebrow">Runtime Gung target</span>
            <h5>{{ model.target.messageType }}</h5>
            <p>Protected operational fields remain visible but cannot be selected.</p>
          </header>
          <div class="contract-tree target-tree">
            <ng-container
              *ngTemplateOutlet="targetFields; context: { $implicit: model.target.fields, depth: 0 }"
            ></ng-container>
          </div>
        </article>
      </section>

      <ng-template #targetFields let-fields let-depth="depth">
        <ng-container *ngFor="let field of fields">
          <button
            type="button"
            class="target-field"
            [class.selected]="selectedTargetField?.path === field.path"
            [class.protected]="!field.mappable"
            [style.--depth]="depth"
            [disabled]="!field.mappable"
            (click)="selectTarget(field)"
          >
            <i
              class="fa-solid"
              [ngClass]="field.kind === 'ARRAY' ? 'fa-list' : field.children.length ? 'fa-folder' : 'fa-code'"
            ></i>
            <span>{{ field.label }}</span>
            <code>{{ field.path }}</code>
            <small>{{ field.kind }} · {{ field.valueType }}</small>
          </button>
          <ng-container
            *ngIf="field.children.length"
            [ngTemplateOutlet]="targetFields"
            [ngTemplateOutletContext]="{ $implicit: field.children, depth: depth + 1 }"
          ></ng-container>
        </ng-container>
      </ng-template>

      <section class="rule-builder">
        <div>
          <span>Selected source</span>
          <code>{{ selectedSourceNode?.path || 'Select a source value' }}</code>
        </div>
        <i class="fa-solid fa-arrow-right"></i>
        <div>
          <span>Selected target</span>
          <code>{{ selectedTargetField?.path || 'Select a target field' }}</code>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          [disabled]="!selectedSourceNode || !selectedTargetField"
          (click)="addMapRule()"
        >
          Add MAP rule
        </button>
      </section>
    </section>

    <section class="workspace-pane" [hidden]="activeWorkspaceTab !== 'instruction'">
      <section class="instruction-editor">
        <header>
          <div>
            <span class="eyebrow">Mapping instruction</span>
            <h5>Editable JSON draft</h5>
            <p>The actions remain available in the toolbar while you edit.</p>
          </div>
        </header>

        <textarea
          class="instruction-json"
          rows="30"
          [(ngModel)]="instructionJson"
          (ngModelChange)="instructionChanged()"
          spellcheck="false"
        ></textarea>

        <div class="validation-result" *ngIf="validation" [class.valid]="validation.valid">
          <strong>{{ validation.valid ? 'Instruction is structurally valid' : 'Instruction needs changes' }}</strong>
          <ul *ngIf="validation.errors.length">
            <li *ngFor="let validationError of validation.errors">{{ validationError }}</li>
          </ul>
          <ul class="warnings" *ngIf="validation.warnings.length">
            <li *ngFor="let validationWarning of validation.warnings">{{ validationWarning }}</li>
          </ul>
        </div>
      </section>
    </section>

    <section class="workspace-pane" [hidden]="activeWorkspaceTab !== 'test'">
      <div class="workspace-empty" *ngIf="!dryRun">
        <i class="fa-solid fa-flask"></i>
        <h5>No dry run yet</h5>
        <p>Run the current instruction against the source without saving or entering the regular EDI flow.</p>
        <button type="button" class="btn btn-primary" [disabled]="dryRunning" (click)="dryRunInstruction()">
          <i class="fa-solid fa-play mr-1"></i>
          Dry run
        </button>
      </div>

      <section class="dry-run-result" *ngIf="dryRun">
        <div class="outdated-result" *ngIf="dryRunOutdated">
          <i class="fa-solid fa-triangle-exclamation"></i>
          This result is from an earlier draft. Run again to test the current instruction.
        </div>
        <nav class="dry-run-sources" *ngIf="dryRunBatch && dryRunBatch.results.length > 1" aria-label="Dry-run sources">
          <button
            type="button"
            *ngFor="let sourceResult of dryRunBatch.results; let resultIndex = index"
            [class.active]="selectedDryRunIndex === resultIndex"
            [class.successful]="sourceResult.dryRun.successful"
            (click)="selectDryRunResult(resultIndex)"
          >
            <i
              class="fa-solid"
              [ngClass]="sourceResult.dryRun.successful ? 'fa-circle-check' : 'fa-circle-exclamation'"
            ></i>
            {{ dryRunResultName(sourceResult) }}
          </button>
        </nav>
        <header [class.successful]="dryRun.successful && !dryRunOutdated">
          <div>
            <span class="eyebrow">
              Dry-run result{{ dryRunBatch ? ' · ' + dryRunResultName(dryRunBatch.results[selectedDryRunIndex]) : '' }}
            </span>
            <h5>{{ dryRun.successful ? 'Mapping completed' : 'Mapping needs changes' }}</h5>
            <p>
              {{
                dryRun.runtimeTyped ? 'Converted through the runtime Gung message class.' : 'Draft JSON result only.'
              }}
              Nothing was saved, sent, or passed into the regular EDI flow.
            </p>
          </div>
          <div class="dry-run-header-actions">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              [attr.aria-expanded]="ruleResultsExpanded"
              (click)="toggleRuleResults()"
            >
              <i class="fa-solid mr-1" [ngClass]="ruleResultsExpanded ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
              {{ ruleResultsExpanded ? 'Fold in rule execution' : 'Show rule execution' }}
            </button>
            <span class="result-status">{{
              dryRunOutdated ? 'Outdated' : dryRun.successful ? 'Successful' : 'Failed'
            }}</span>
          </div>
        </header>

        <div class="dry-run-messages" *ngIf="dryRun.errors.length || dryRun.warnings.length">
          <ul class="errors" *ngIf="dryRun.errors.length">
            <li *ngFor="let dryRunError of dryRun.errors">{{ dryRunError }}</li>
          </ul>
          <ul class="warnings" *ngIf="dryRun.warnings.length">
            <li *ngFor="let dryRunWarning of dryRun.warnings">{{ dryRunWarning }}</li>
          </ul>
        </div>

        <div class="dry-run-grid" [class.with-rule-results]="ruleResultsExpanded">
          <article class="result-json">
            <h6>Generated {{ model.target.messageType }}</h6>
            <pre *ngIf="dryRunJson; else noDryRunOutput">{{ dryRunJson }}</pre>
            <ng-template #noDryRunOutput><p>No target result was generated.</p></ng-template>
          </article>

          <article class="rule-results" *ngIf="ruleResultsExpanded">
            <h6>Rule execution</h6>
            <div class="rule-result" *ngFor="let rule of dryRun.ruleResults" [ngClass]="rule.status.toLowerCase()">
              <div>
                <strong>{{ rule.ruleId || 'Unnamed rule' }}</strong>
                <span class="rule-status">{{ rule.status }}</span>
              </div>
              <code>{{ rule.operation }} · {{ rule.targetPath || 'No target path' }}</code>
              <p>{{ rule.message }}</p>
              <small>{{ rule.matchedSourceNodes }} source match(es) · {{ rule.writtenValues }} value(s) written</small>
            </div>
            <p *ngIf="!dryRun.ruleResults.length">No rules were executed.</p>
          </article>
        </div>
      </section>
    </section>

    <section class="workspace-pane history-pane" [hidden]="activeWorkspaceTab !== 'history'">
      <section class="model-save-card">
        <label>
          <span>Model name</span>
          <input
            class="form-control"
            maxlength="160"
            [(ngModel)]="modelName"
            [disabled]="!!savedModel || savingModel"
            placeholder="Name this mapping model"
          />
          <small *ngIf="savedModel">The name is retained across versions.</small>
          <small *ngIf="!savedModel">Suggested from the detected source; edit it before the first save.</small>
        </label>
        <div class="save-version-info">
          <span>Version to create</span>
          <strong>v{{ savedModel ? savedModel.version + 1 : 1 }}</strong>
          <small
            >{{ sessionPrompts.length }} new prompt{{ sessionPrompts.length === 1 ? '' : 's' }} will be recorded</small
          >
        </div>
      </section>

      <section class="history-card">
        <header>
          <span class="eyebrow">Prompt history</span>
          <h5>
            {{ savedPrompts.length + sessionPrompts.length }} recorded prompt{{
              savedPrompts.length + sessionPrompts.length === 1 ? '' : 's'
            }}
          </h5>
        </header>
        <ol *ngIf="savedPrompts.length || sessionPrompts.length; else noPromptHistory">
          <li *ngFor="let prompt of savedPrompts">
            <p>{{ prompt.text }}</p>
            <small>v{{ prompt.modelVersion }} · {{ prompt.user }} · {{ prompt.recordedAt | date: 'medium' }}</small>
          </li>
          <li *ngFor="let prompt of sessionPrompts">
            <p>{{ prompt }}</p>
            <small>Pending save in v{{ savedModel ? savedModel.version + 1 : 1 }}</small>
          </li>
        </ol>
        <ng-template #noPromptHistory
          ><p class="empty-copy">No AI guidance has been recorded for this draft.</p></ng-template
        >
      </section>
    </section>
  </ng-container>
</div>
